import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Box } from "@mui/material"
import {
  PreparePanel,
  type PrepareState,
  type PrepSection,
  type SourceMetaItem,
} from "@ksana/vira-ui"
import {
  ClientFragment,
  useNudgesScheduleByUserQuery,
  useQuestionnairesScheduleByUserQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useSession } from "../../hooks/useSession"
import { Role } from "../../utils/constants"
import { listClientProgressNotes } from "../../utils/transcriptApi"
import {
  askViraAboutClient,
  buildSessionPrepSources,
  clearCachedSessionPrep,
  generateSessionPrep,
  loadCachedSessionPrep,
  type SessionPrepMode,
  type SessionPrepRecord,
} from "../../utils/sessionPrepApi"
import PrepareInsights from "./PrepareInsights"
import PrepareSignals from "./PrepareSignals"

type ClientDetailPrepareProps = {
  client: ClientFragment
  clientName: string
  clientFirstName: string
  practitionerId: string
}

/** Prevents StrictMode double-mount from firing two auto-prep APIM calls. */
const autoPrepStartedKeys = new Set<string>()

function clientHasSensingData(client: ClientFragment): boolean {
  return (
    (client.mood_measurements?.length ?? 0) > 0 ||
    (client.daily_features?.length ?? 0) > 0 ||
    (client.vw_daily_features_sleep?.length ?? 0) > 0 ||
    (client.vw_daily_features_trend?.length ?? 0) > 0 ||
    (client.activity_rating?.length ?? 0) > 0
  )
}

/**
 * Loop Prepare pane: auto-generate session prep when no 12h cache (deck
 * “ready on open”), keep Regenerate, show source meta + signals + Ask Vira,
 * and end with Latest Insights (“best days”).
 */
export default function ClientDetailPrepare({
  client,
  clientName,
  clientFirstName,
  practitionerId,
}: ClientDetailPrepareProps) {
  const clientId = client.id
  const { session } = useSession()
  const jwt = session?.jwt || ""
  const supervisorId =
    session?.role === Role.WEB_ROLE ? session.userId : undefined
  const graphqlClient = useGraphQLClient()

  const {
    data: nudgesData,
    isSuccess: nudgesReady,
    isError: nudgesError,
  } = useNudgesScheduleByUserQuery(graphqlClient, {
    user_id: clientId,
  })
  const {
    data: questionnairesData,
    isSuccess: questionnairesReady,
    isError: questionnairesError,
  } = useQuestionnairesScheduleByUserQuery(graphqlClient, {
    user_id: clientId,
  })

  const schedulesReady = nudgesReady && questionnairesReady
  const schedulesFailed = nudgesError || questionnairesError

  const hasClientData = useMemo(() => clientHasSensingData(client), [client])
  // Only compute hasPlan after both schedule queries succeed — pending/error
  // must not be treated as confirmed absence of a plan.
  const hasPlan = useMemo(() => {
    if (!schedulesReady) {
      return false
    }
    const nudges = nudgesData?.nudge_schedules?.length ?? 0
    const questionnaires =
      questionnairesData?.questionnaire_schedules?.length ?? 0
    return nudges + questionnaires > 0
  }, [schedulesReady, nudgesData, questionnairesData])

  const [panelState, setPanelState] = useState<PrepareState>("initial")
  const [prep, setPrep] = useState<SessionPrepRecord | null>(null)
  const [lastNoteDate, setLastNoteDate] = useState<string | null>(null)
  const [hasPriorNote, setHasPriorNote] = useState(false)
  const [notesReady, setNotesReady] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [progressValue, setProgressValue] = useState<number | undefined>(
    undefined
  )
  const [askLoading, setAskLoading] = useState(false)
  const [askAnswer, setAskAnswer] = useState<string | null>(null)
  const [askError, setAskError] = useState<string | null>(null)
  const lastNoteDateRef = useRef(lastNoteDate)
  const hasPriorNoteRef = useRef(hasPriorNote)
  const hasPlanRef = useRef(hasPlan)
  const hasClientDataRef = useRef(hasClientData)
  const prepRef = useRef(prep)
  lastNoteDateRef.current = lastNoteDate
  hasPriorNoteRef.current = hasPriorNote
  hasPlanRef.current = hasPlan
  hasClientDataRef.current = hasClientData
  prepRef.current = prep

  useEffect(() => {
    setAskAnswer(null)
    setAskError(null)
    setNotesReady(false)
    const cached = loadCachedSessionPrep(clientId, practitionerId)
    if (cached) {
      setPrep(cached)
      setPanelState("complete")
    } else {
      setPrep(null)
      // Show loading immediately for deck “ready on open” while notes resolve
      // and auto-generate starts (avoids a Generate-button flash).
      setPanelState(practitionerId ? "generating" : "initial")
      setProgressValue(practitionerId ? 8 : undefined)
    }
  }, [clientId, practitionerId])

  useEffect(() => {
    if (!jwt || !clientId) {
      setHasPriorNote(false)
      setLastNoteDate(null)
      setNotesReady(true)
      return
    }
    let ignore = false
    const loadNotes = async () => {
      try {
        const notes = await listClientProgressNotes(jwt, clientId)
        if (ignore) {
          return
        }
        const withBody = notes.filter((note) =>
          Boolean(note.soapText || note.birpText)
        )
        setHasPriorNote(withBody.length > 0)
        // API lists newest-first by updated_at; use the latest note with body.
        setLastNoteDate(withBody[0]?.updatedAt ?? null)
      } catch {
        if (!ignore) {
          setHasPriorNote(false)
          setLastNoteDate(null)
        }
      } finally {
        if (!ignore) {
          setNotesReady(true)
        }
      }
    }
    void loadNotes()
    return () => {
      ignore = true
    }
  }, [jwt, clientId])

  const sources: SourceMetaItem[] = useMemo(() => {
    if (prep?.sources?.length) {
      return prep.sources
    }
    return buildSessionPrepSources({
      lastNoteDate,
      hasPriorNote,
      hasPlan,
      hasClientData,
    })
  }, [prep, lastNoteDate, hasPriorNote, hasPlan, hasClientData])

  const sections: PrepSection[] = prep?.sections ?? []

  const releaseAutoPrepGuard = useCallback(() => {
    autoPrepStartedKeys.delete(`${practitionerId}:${clientId}`)
  }, [practitionerId, clientId])

  const runGenerate = useCallback(
    async (mode: SessionPrepMode) => {
      if (!practitionerId) {
        setErrorMessage("Sign in as a practitioner to generate session prep.")
        return
      }
      setErrorMessage(null)
      setAskAnswer(null)
      setAskError(null)
      setPanelState("generating")
      setProgressValue(12)
      const tick = window.setInterval(() => {
        setProgressValue((prev) => {
          if (prev == null) {
            return 18
          }
          return Math.min(prev + 7, 88)
        })
      }, 700)

      const hadPrep = Boolean(prepRef.current)

      try {
        const record = await generateSessionPrep({
          mode,
          clientId,
          clientFirstName: clientFirstName || clientName || "this client",
          practitionerId,
          supervisorId,
          lastNoteDate: lastNoteDateRef.current,
          hasPriorNote: hasPriorNoteRef.current,
          hasPlan: hasPlanRef.current,
          hasClientData: hasClientDataRef.current,
        })
        // Empty results are not cached — release the auto-prep guard so a
        // remount can retry instead of spinning forever on generating.
        if (record.sections.length === 0) {
          releaseAutoPrepGuard()
        }
        setPrep(record)
        setProgressValue(100)
        setPanelState("complete")
      } catch (error) {
        releaseAutoPrepGuard()
        const message =
          error instanceof Error
            ? error.message
            : "Unable to generate session prep."
        setErrorMessage(message)
        setPanelState(hadPrep ? "complete" : "initial")
      } finally {
        window.clearInterval(tick)
        setProgressValue(undefined)
      }
    },
    [
      clientFirstName,
      clientId,
      clientName,
      practitionerId,
      releaseAutoPrepGuard,
      supervisorId,
    ]
  )

  useEffect(() => {
    if (schedulesFailed && !loadCachedSessionPrep(clientId, practitionerId)) {
      setProgressValue(undefined)
      setPanelState("initial")
      setErrorMessage("Unable to load the client's plan schedule.")
    }
  }, [schedulesFailed, clientId, practitionerId])

  useEffect(() => {
    // Wait for notes and both schedule queries so hasPlan is real, not pending.
    if (!notesReady || !schedulesReady || !practitionerId) {
      return
    }
    if (loadCachedSessionPrep(clientId, practitionerId)) {
      return
    }
    const key = `${practitionerId}:${clientId}`
    if (autoPrepStartedKeys.has(key)) {
      return
    }
    autoPrepStartedKeys.add(key)
    void runGenerate("session_prep")
  }, [notesReady, schedulesReady, practitionerId, clientId, runGenerate])

  const handleRegenerate = useCallback(() => {
    clearCachedSessionPrep(clientId, practitionerId)
    releaseAutoPrepGuard()
    prepRef.current = null
    setPrep(null)
    void runGenerate("session_prep")
  }, [clientId, practitionerId, releaseAutoPrepGuard, runGenerate])

  const handleAsk = useCallback(
    async (question: string) => {
      if (!practitionerId) {
        setAskError("Sign in as a practitioner to ask Vira.")
        return
      }
      setAskError(null)
      setAskLoading(true)
      setAskAnswer(null)
      try {
        const answer = await askViraAboutClient({
          clientId,
          practitionerId,
          supervisorId,
          clientFirstName: clientFirstName || clientName || "this client",
          question,
        })
        setAskAnswer(answer)
      } catch (error) {
        setAskError(
          error instanceof Error ? error.message : "Unable to ask Vira."
        )
      } finally {
        setAskLoading(false)
      }
    },
    [clientFirstName, clientId, clientName, practitionerId, supervisorId]
  )

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        maxWidth: { xs: "100%", md: 760 },
        mx: "auto",
      }}
    >
      {errorMessage ? (
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      ) : null}
      {askError ? (
        <Alert severity="error" onClose={() => setAskError(null)}>
          {askError}
        </Alert>
      ) : null}
      <PreparePanel
        state={panelState}
        clientName={clientName}
        sources={sources}
        sections={sections}
        progressValue={progressValue}
        progressLabel="Writing your prep"
        signals={
          panelState === "complete" ? (
            <PrepareSignals client={client} />
          ) : undefined
        }
        askLoading={askLoading}
        askAnswer={askAnswer}
        onAsk={panelState === "complete" ? handleAsk : undefined}
        onGenerate={() => {
          void runGenerate("session_prep")
        }}
        onBriefSummary={() => {
          void runGenerate("brief_summary")
        }}
        onRegenerate={handleRegenerate}
      />
      <PrepareInsights
        client={client}
        clientFirstName={clientFirstName || clientName}
      />
    </Box>
  )
}
